import { forwardRef, useMemo, useState } from "react";

import { Box } from "@mui/material";
import dynamic from "next/dynamic";

import { useFetchHeartsAnimation } from "./useFetchHeartsAnimation";

import { FormTab, FormTabProps } from "@/forms-design-system/FormTab";
import { Heart } from "@/icons/other";

const Lottie = dynamic(() => import("react-lottie"), { ssr: false });

type HeartsFormTabProps = Omit<FormTabProps, "icon" | "backgroundContent">;

export const HeartsFormTab = forwardRef<HTMLDivElement, HeartsFormTabProps>(
  ({ disabled = false, children, ...rest }, ref) => {
    const [animationKey, setAnimationKey] = useState(0);

    const animated = animationKey > 0;

    const heartsAnimationData = useFetchHeartsAnimation();
    const isCypressTest = typeof window !== "undefined" && !!window?.Cypress;

    const handleOnMouseEnter = () => {
      if (!disabled && !isCypressTest) {
        setAnimationKey((prev) => prev + 1);
      }
    };

    const lottieOptions = useMemo(() => {
      return {
        animationData: heartsAnimationData,
        autoplay: animated,
        loop: false,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid slice",
        },
      };
    }, [heartsAnimationData, animated]);

    const heartsAnimation = (
      <>
        {animated && heartsAnimationData && !disabled && !isCypressTest && (
          <Box
            key={animationKey}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              opacity: 0.5,
            }}
          >
            <Lottie options={lottieOptions} width="100%" height="100%" isClickToPauseDisabled />
          </Box>
        )}
      </>
    );

    return (
      <FormTab
        {...rest}
        ref={ref}
        icon={<Heart />}
        backgroundContent={heartsAnimation}
        onMouseEnter={handleOnMouseEnter}
      >
        {children}
      </FormTab>
    );
  }
);

HeartsFormTab.displayName = "HeartsFormTab";
